import LegalPageLayout from '../components/LegalPageLayout';

export default function PrivacyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="February 7, 2026">
      <p>
        Family Waffle ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
        explains how we collect, use, disclose, and safeguard your information when you use our mobile
        application and related services (the "Service"). Please read this Privacy Policy carefully. By
        using the Service, you agree to the collection and use of information in accordance with this
        policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <p>We collect information that you voluntarily provide when you register for and use the Service:</p>
      <ul>
        <li><strong>Account Information:</strong> Email address, display name, and password (stored as a
          secure, one-way hash using an industry-standard algorithm designed to resist brute-force
          attacks).</li>
        <li><strong>Profile Information:</strong> Profile photo (if provided), phone number and country
          code (provided during onboarding).</li>
        <li><strong>Connection Data:</strong> Information about your connections and lists within the app,
          including invite codes and invite links you generate or use.</li>
        <li><strong>Nicknames:</strong> Private nicknames you assign to your connections. These are visible
          only to you and are not shared with other users.</li>
        <li><strong>User-Entered Metadata:</strong> Optional location labels and dates you add to posts.
          Note that location data is entered manually by you and is not derived from GPS or device location
          services.</li>
        <li><strong>Account Deletion Feedback:</strong> If you choose to delete your account, you may
          optionally provide a reason and feedback, which is stored for product improvement purposes.</li>
      </ul>

      <h3>1.2 Information Collected Automatically</h3>
      <p>When you use the Service, we automatically collect certain information:</p>
      <ul>
        <li><strong>Device and Session Information:</strong> When you sign in, we record a device session
          that includes device name, device type (iOS/Android/web), operating system version, app version,
          and the date and time of sign-in. Session heartbeats update the last active timestamp. We may
          also derive approximate location (city, country) from your IP address for session identification
          purposes.</li>
        <li><strong>Crash Reports and Diagnostics:</strong> Device type, operating system version, app
          version, and error data are collected via our error reporting provider for stability
          improvements. Crash reports do not include your encrypted content, personal messages, photos,
          or encryption keys.</li>
        <li><strong>Push Notification Tokens:</strong> Device-specific tokens for delivering push
          notifications (if you opt in). Tokens are associated with your user ID and device identifier.</li>
      </ul>

      <h3>1.3 Consent Records</h3>
      <p>
        When you accept our Terms of Service and Privacy Policy (during registration and when re-accepting
        after updates), we record an immutable consent record that includes the document type, document
        version, your app version, timestamp, IP address, and user agent. These records cannot be modified
        or deleted and serve as an audit trail for regulatory compliance.
      </p>

      <h3>1.4 Information We Do NOT Collect</h3>
      <p>
        Family Waffle uses end-to-end encryption for photos, videos, voice messages, captions, comments,
        and other shared content.
        <strong> We cannot access, read, decrypt, or view your encrypted content.</strong> This includes:
      </p>
      <ul>
        <li>Photos and videos you share with connections.</li>
        <li>Captions and text attached to shared posts.</li>
        <li>Voice messages and voice captions.</li>
        <li>Text comments and replies on encrypted posts.</li>
        <li>Emoji reactions on posts.</li>
      </ul>
      <p>
        Your encryption keys are generated and stored securely on your devices using hardware-backed
        secure storage. We do not have access to your decryption keys. A password-protected copy of
        your account key is stored on our servers to enable multi-device sign-in, but it cannot be
        decrypted without your password, which we never store in plaintext.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li><strong>Provide and maintain the Service:</strong> Create and manage your account, facilitate
          connections between users, deliver encrypted content to designated recipients, and manage your
          subscription.</li>
        <li><strong>Send notifications:</strong> Deliver push notifications about new shares, comments,
          replies, connection requests, and important account updates (with your consent).</li>
        <li><strong>Manage sessions:</strong> Track active device sessions to enable multi-device usage
          and allow you to remotely sign out devices for security.</li>
        <li><strong>Improve the Service:</strong> Analyze crash reports and error data to fix bugs,
          improve performance, and develop new features. We do not analyze your encrypted content for
          any purpose.</li>
        <li><strong>Provide support:</strong> Respond to your inquiries and resolve technical issues.</li>
        <li><strong>Enforce our Terms:</strong> Detect and prevent fraud, abuse, and violations of our
          Terms of Service.</li>
        <li><strong>Maintain compliance:</strong> Record and store consent records to demonstrate
          regulatory compliance with GDPR and other data protection laws.</li>
      </ul>
      <p>
        We do <strong>not</strong> use your information for advertising purposes. We do <strong>not</strong> sell
        your personal information to third parties.
      </p>

      <h2>3. Third-Party Services</h2>
      <p>
        We use the following categories of third-party service providers to operate and improve the
        Service. Each provider has access only to the minimum information necessary to perform its
        function:
      </p>
      <ul>
        <li>
          <strong>Cloud Infrastructure and Authentication Provider (United States):</strong> Provides
          user authentication (processes your email and hashed password), database hosting (stores
          profiles, connections, post metadata, lists, session records, consent records, notification
          preferences, and subscription data), and serverless computing. This provider does not have
          access to your encryption keys and cannot decrypt your content.
        </li>
        <li>
          <strong>Content Delivery and Storage Provider (United States / Global):</strong> Provides
          API hosting, encrypted media file storage (photos, videos, voice messages, profile pictures),
          and content delivery services. This provider stores encrypted data and cannot decrypt your
          content.
        </li>
        <li>
          <strong>Subscription Management Provider (United States):</strong> Manages subscription
          billing and entitlements. Receives your anonymous app user ID, subscription status, product
          identifiers, and purchase receipts from the Apple App Store. This provider does not receive
          your email address, name, or personal content from us.
        </li>
        <li>
          <strong>Error Reporting Provider (United States):</strong> Provides crash reporting and error
          tracking to improve app stability. Receives device information (device type, OS version, app
          version) and error data. This provider does not receive your encrypted content, personal
          messages, photos, or encryption keys. Crash data is retained for up to 90 days.
        </li>
        <li>
          <strong>Apple (Apple Inc.):</strong> Processes subscription billing through the App Store.
          Apple receives your payment information and purchase receipts directly. We do not receive or
          store your payment card information. Apple's handling of your data is governed by Apple's
          own privacy policy.
        </li>
      </ul>

      <h2>4. Public Share Links</h2>
      <p>
        The Service allows you to create public share links for individual posts. When you generate
        a public share link:
      </p>
      <ul>
        <li>The encrypted content associated with that post is made available for decryption and viewing
          by anyone with the link.</li>
        <li><strong>Content accessed via public share links is no longer protected by end-to-end encryption
          for those viewers.</strong></li>
        <li>Public share link viewers do not need a Family Waffle account to view the content.</li>
        <li>We do not track or log who views content via public share links beyond standard web server
          logs.</li>
        <li>You can revoke a public share link at any time from within the app, after which the content
          will no longer be accessible via that link.</li>
      </ul>

      <h2>5. Blocked Users</h2>
      <p>
        When you block a user, we store the blocked relationship in our database. Blocked users cannot
        send you connection requests, view your content, or interact with you. The blocked user is not
        notified that they have been blocked. You can view and manage your blocked users list in the
        app settings.
      </p>

      <h2>6. Data Retention and Deletion</h2>
      <p>
        We retain your personal information only for as long as necessary to provide the Service and
        fulfill the purposes described in this Privacy Policy.
      </p>
      <ul>
        <li><strong>Active Accounts:</strong> Your account information and encrypted content are retained
          for as long as your account is active.</li>
        <li><strong>Deleted Accounts:</strong> When you request account deletion, your account enters a
          30-day grace period during which you can cancel the deletion by signing in. After the grace
          period expires, we permanently delete your profile information, authentication credentials,
          connections, encrypted content, and associated files from our servers, including from cloud
          storage. This process includes deletion of profile pictures, post media, and comment
          assets.</li>
        <li><strong>Consent Records:</strong> Consent records are retained as long as necessary to
          demonstrate compliance with applicable data protection laws, even after account deletion.
          Consent records are immutable and cannot be modified.</li>
        <li><strong>Crash Reports:</strong> Error and crash data is retained for up to 90 days for
          debugging purposes, then automatically deleted.</li>
        <li><strong>Backups:</strong> Encrypted data may persist in system backups for up to 30 days
          after deletion before being fully purged.</li>
        <li><strong>Session Records:</strong> Device session records are retained for security audit
          purposes after sign-out.</li>
      </ul>

      <h2>7. On-Device Data Storage</h2>
      <p>
        The Family Waffle app stores the following data locally on your device:
      </p>
      <ul>
        <li><strong>Secure Device Storage:</strong> Your encryption keys are stored in your device's
          secure storage facility, which provides hardware-backed protection.</li>
        <li><strong>App Cache:</strong> Decrypted content may be temporarily cached on your device for
          performance. You can clear the app cache from the Settings screen within the app.</li>
        <li><strong>Session Data:</strong> Your current session identifier is stored locally to maintain
          your signed-in state.</li>
      </ul>
      <p>
        We do not use cookies or local storage on our web pages for tracking purposes. Our web pages
        use only minimal, essential functionality.
      </p>

      <h2>8. Children's Privacy (COPPA Compliance)</h2>
      <p>
        The Service is not intended for children under the age of 13. We do not knowingly collect
        personal information from children under 13. If you are a parent or guardian and you believe your
        child under 13 has provided us with personal information, please contact us at{' '}
        <a href="mailto:privacy@familywaffle.com">privacy@familywaffle.com</a>. If we discover that we
        have collected personal information from a child under 13 without verification of parental
        consent, we will take steps to delete that information as quickly as possible.
      </p>

      <h2>9. Your Rights</h2>
      <p>
        Depending on your location, you may have certain rights regarding your personal information:
      </p>
      <ul>
        <li><strong>Access:</strong> You can request a copy of the personal information we hold about you.</li>
        <li><strong>Correction:</strong> You can update your account information at any time through the
          app settings (profile name, profile photo, phone number), or request that we correct inaccurate
          information.</li>
        <li><strong>Deletion:</strong> You can delete your account at any time through the app settings,
          or request deletion by contacting us. Upon deletion (after the 30-day grace period), all your
          personal data will be permanently removed.</li>
        <li><strong>Data Portability:</strong> You can request a machine-readable export of the personal
          information we hold about you. Note that encrypted content can only be decrypted with your
          encryption keys.</li>
        <li><strong>Objection:</strong> You can object to certain processing of your information.</li>
        <li><strong>Withdrawal of Consent:</strong> Where processing is based on consent (such as push
          notifications), you can withdraw your consent at any time by adjusting your notification
          settings in the app or your device settings. Withdrawal of consent does not affect the
          lawfulness of processing carried out before withdrawal.</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{' '}
        <a href="mailto:privacy@familywaffle.com">privacy@familywaffle.com</a>. We will respond to your
        request within 30 days.
      </p>

      <h2>10. GDPR (European Economic Area)</h2>
      <p>
        If you are located in the European Economic Area (EEA), you have additional rights under the
        General Data Protection Regulation (GDPR):
      </p>

      <h3>10.1 Legal Basis for Processing</h3>
      <p>We process your personal data based on the following legal bases:</p>
      <ul>
        <li><strong>Contract Performance (Article 6(1)(b)):</strong> Processing your account information,
          profile data, connections, and encrypted content is necessary to provide the Service as described
          in our Terms of Service.</li>
        <li><strong>Consent (Article 6(1)(a)):</strong> Push notifications are sent only with your
          explicit opt-in consent. You can withdraw consent at any time by disabling notifications.</li>
        <li><strong>Legitimate Interests (Article 6(1)(f)):</strong> Crash reporting and error tracking
          is processed based on our legitimate interest in maintaining and improving the stability and
          security of the Service. Session tracking is based on our legitimate interest in providing
          account security features (remote sign-out, unauthorized access detection).</li>
        <li><strong>Legal Obligation (Article 6(1)(c)):</strong> Consent records and certain account data
          are retained to comply with legal obligations, including demonstrating GDPR compliance.</li>
      </ul>

      <h3>10.2 International Data Transfers</h3>
      <p>
        Your information is transferred to and processed in the United States by our third-party service
        providers. Specifically:
      </p>
      <ul>
        <li>Our cloud infrastructure and authentication provider hosts data in the United States.</li>
        <li>Our content delivery and storage provider processes requests at global edge locations,
          with primary storage in the United States.</li>
        <li>Our subscription management, error reporting, and billing providers (including Apple)
          process data in the United States.</li>
      </ul>
      <p>
        When transferring data outside the EEA, we rely on appropriate safeguards including standard
        contractual clauses (SCCs) approved by the European Commission. Because your content is
        end-to-end encrypted, the data stored on our servers is unreadable without your encryption
        keys, providing an additional layer of protection for international transfers.
      </p>

      <h3>10.3 Data Protection Contact</h3>
      <p>
        For GDPR-related inquiries, please contact us at{' '}
        <a href="mailto:privacy@familywaffle.com">privacy@familywaffle.com</a>.
      </p>

      <h3>10.4 Supervisory Authority</h3>
      <p>
        You have the right to lodge a complaint with a data protection supervisory authority in your
        country of residence if you believe our processing of your personal data violates the GDPR.
      </p>

      <h2>11. CCPA (California Residents)</h2>
      <p>
        If you are a California resident, you have additional rights under the California Consumer
        Privacy Act (CCPA) and the California Privacy Rights Act (CPRA):
      </p>

      <h3>11.1 Categories of Personal Information Collected</h3>
      <p>In the preceding 12 months, we have collected the following categories of personal information:</p>
      <ul>
        <li><strong>Identifiers:</strong> Email address, display name, phone number, device identifiers,
          IP address (derived from sessions).</li>
        <li><strong>Commercial Information:</strong> Subscription status, purchase history (processed by
          Apple and our subscription management provider).</li>
        <li><strong>Internet or Electronic Network Activity:</strong> Device type, OS version, app version,
          crash reports, session activity timestamps.</li>
        <li><strong>Sensory Data:</strong> Profile photos (voluntarily provided).</li>
      </ul>
      <p>
        We do not collect protected classification characteristics, biometric information, geolocation
        data (location labels on posts are manually entered), professional or employment information,
        or education information.
      </p>

      <h3>11.2 Your CCPA Rights</h3>
      <ul>
        <li><strong>Right to Know:</strong> You can request that we disclose the categories and specific
          pieces of personal information we have collected about you, the categories of sources from which
          the information was collected, the business or commercial purpose for collecting the
          information, and the categories of third parties with whom we share the information.</li>
        <li><strong>Right to Delete:</strong> You can request the deletion of your personal information,
          subject to certain exceptions.</li>
        <li><strong>Right to Correct:</strong> You can request that we correct inaccurate personal
          information.</li>
        <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for
          exercising any of your CCPA rights.</li>
        <li><strong>No Sale or Sharing of Personal Information:</strong> We do not sell your personal
          information to third parties. We do not share your personal information for cross-context
          behavioral advertising. We have not sold or shared personal information in the preceding 12
          months.</li>
      </ul>
      <p>
        To exercise your CCPA rights, please contact us at{' '}
        <a href="mailto:privacy@familywaffle.com">privacy@familywaffle.com</a>.
      </p>

      <h2>12. Data Security</h2>
      <p>
        We take the security of your information seriously and implement appropriate technical and
        organizational measures to protect your data, including:
      </p>
      <ul>
        <li>End-to-end encryption using industry-standard authenticated encryption for all shared
          photos, videos, voice messages, captions, and comments.</li>
        <li>Secure key exchange protocols for establishing shared secrets between connections.</li>
        <li>Industry-standard key derivation to protect password-based key access.</li>
        <li>Encrypted data storage and transmission (TLS/SSL) for all API communications.</li>
        <li>Secure password hashing and authentication.</li>
        <li>Database-level and application-level access control policies to ensure users can only
          access their own data.</li>
        <li>Hardware-backed secure storage for encryption keys on your device.</li>
        <li>Regular security reviews and updates.</li>
      </ul>
      <p>
        However, no method of electronic transmission or storage is 100% secure. While we strive to use
        commercially acceptable means to protect your personal information, we cannot guarantee its
        absolute security.
      </p>

      <h2>13. Data Breach Notification</h2>
      <p>
        In the event of a data breach that affects your personal information, we will notify you and
        the relevant supervisory authorities as required by applicable law. For GDPR-covered individuals,
        we will notify the relevant supervisory authority within 72 hours of becoming aware of a breach
        that is likely to result in a risk to your rights and freedoms. We will notify affected individuals
        without undue delay when the breach is likely to result in a high risk to your rights and freedoms.
      </p>
      <p>
        Because your content is end-to-end encrypted, a breach of our servers would not expose the
        contents of your photos, videos, voice messages, or comments, as this data is unreadable without
        your encryption keys.
      </p>

      <h2>14. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will revise the "Last
        updated" date at the top of this page and notify you of material changes through the app or by
        other means. Your continued use of the Service after any changes constitutes your acceptance of
        the updated Privacy Policy. When material changes are made, we may require you to re-accept the
        updated Privacy Policy, and this re-acceptance will be recorded in our consent audit log.
      </p>

      <h2>15. Contact Us</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy or our data practices, please
        contact us at:
      </p>
      <p>
        <a href="mailto:privacy@familywaffle.com">privacy@familywaffle.com</a>
      </p>
    </LegalPageLayout>
  );
}
